<template>
  <div>
    <b-card-actions
      title="Filters"
      action-collapse
      collapsed
    >
      <b-row>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="Created Date"
            label-for="basicInput"
          >
            <div class="position-relative">
              <AdvancedDateFilter
                :date-value="filters['created_at']"
                :opens="'right'"
                @update="updateDateRange('created_at', $event)"
              />
            </div>
          </b-form-group>
        </b-col>
      </b-row>
    </b-card-actions>
    <b-card no-body>
      <div class="m-2">
        <b-row cols="12">
          <b-col
            md="12"
            lg="4"
            class="d-block d-sm-flex flex-wrap justify-content-start align-items-center mb-1 mb-md-0"
          />
          <b-col
            class="d-flex align-items-center justify-content-center mb-1 mb-md-0"
          />
          <b-col
            class="d-flex justify-content-end align-items-center mb-1 mb-md-0"
          >
            <ReportTableButtons
              :items="rows"
              :json_fieldz="json_fields"
              :name="`SortingActivityLog - Page ${page}.xls`"
              :bulkname="`SortingActivityLog.xls`"
              :fetch="getSortingActivityListNoPagination"
              @refresh="onClickRefresh"
            />
          </b-col>
        </b-row>
      </div>

      <b-row>
        <b-col>
          <b-overlay
            id="overlay-background"
            blur="10px"
            opacity="0.30"
            rounded="sm"
            variant="light"
          >
            <b-table
              striped
              hover
              responsive
              :per-page="perPage"
              :items="rows"
              :fields="fields"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              :sort-direction="sortDirection"
              show-empty
              sticky-header="100vh"
              class="hide-vertical-scroll"
              @sort-changed="sortChanged"
            >
              <template #empty>
                <TableDataFetching
                  :rows="rows"
                  :data-loading="dataLoading"
                />
              </template>
              <template #cell(type)="data">
                {{ data.value ? data.value.replace(/_/g, ' ') : '' }}
              </template>
              <template #cell(created_user)="data">
                <div class="d-flex align-items-center">
                  <div>
                    <div class="font-weight-bolder">
                      {{ `${data.item.created_by ? data.item.created_by.first_name : 'N/A'} ${data.item.created_by && data.item.created_by.last_name ? data.item.created_by.last_name : ''}` }}
                    </div>
                    <div class="font-small-2">
                      {{ data.item.created_by && data.item.created_by.email ? data.item.created_by.email : '' }}
                    </div>
                    <div class="font-small-2">
                      {{ data.item.created_by && data.item.created_by.reference_id ? data.item.created_by.reference_id : '' }}
                    </div>
                  </div>
                </div>
              </template>
              <!-- Old Data Column -->
              <template #cell(old_data)="data">
                <div v-if="getFormattedLogArray(data.value).length === 0">
                  N/A
                </div>
                <div
                  v-for="(line, index) in getFormattedLogArray(data.value)"
                  :key="index"
                >
                  {{ line }}
                </div>
              </template>

              <!-- New Data Column -->
              <template #cell(new_data)="data">
                <div v-if="getFormattedLogArray(data.value).length === 0">
                  N/A
                </div>
                <div
                  v-for="(line, index) in getFormattedLogArray(data.value)"
                  :key="index"
                >
                  {{ line }}
                </div>
              </template>
              <template #cell(created_at)="data">
                {{ __dateTimeFormatter(data.value) }}
              </template>
            </b-table>
          </b-overlay>

          <div class="mx-2 mb-2">
            <b-row
              cols="12"
            >
              <b-col
                class="d-flex align-items-center justify-content-sm-start"
                md="3"
              >
                <label class="width-75">Per page</label>
                <v-select
                  v-model="perPage"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="pageOptions"
                  :clearable="false"
                  class="per-page-selector d-inline-block width-100"
                />
              </b-col>
              <!-- Pagination -->
              <b-col
                class="d-flex align-items-center justify-content-sm-end"
                md="9"
              >
                <span class="text-muted mr-1">Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} entries</span>
                <b-pagination
                  v-model="page"
                  :per-page="perPage"
                  :total-rows="(meta.total)? meta.total : 0"
                  class="mb-0 mt-1 mt-sm-0"
                  first-number
                  last-number
                  next-class="next-item"
                  prev-class="prev-item"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BTable,
  BCard,
  BFormGroup,
  BPagination,
  BOverlay,
  VBTooltip,
} from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import vSelect from 'vue-select'
import AdvancedDateFilter from '@/components/AdvancedDateFilter/AdvancedDateFilter.vue'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import Filter from '@core/mixins/filter'
import Ripple from 'vue-ripple-directive'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
// eslint-disable-next-line import/no-cycle
import store from '@/store'

const sortingRepository = RepositoryFactory.get('sorting')

export default {
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  components: {
    BRow,
    BCol,
    BTable,
    BCard,
    BCardActions,
    BFormGroup,
    BPagination,
    BOverlay,
    vSelect,
    AdvancedDateFilter,
  },
  mixins: [Filter, SuccessMessage, ErrorMessage],
  data() {
    return {
      // Table Info
      loadingResponse: false,
      dataLoading: false,
      filters: {
        created_at: {
          startDate: null,
          endDate: null,
        },
      },
      total: 0,
      meta: {},
      sort: '',
      perPage: 20,
      pageOptions: [10, 20, 50],
      totalRows: 1,
      page: 1,
      sortBy: '',
      sortDesc: false,
      sortDirection: 'asc',
      filterName: '',
      fields: [
        {
          key: 'type',
          label: 'Log Type',
          sortable: true,
        },
        {
          key: 'created_user',
          label: 'User',
          sortable: true,
        },
        {
          key: 'old_data',
          label: 'Old Data',
          sortable: true,
        },
        {
          key: 'new_data',
          label: 'New Data',
          sortable: true,
        },
        {
          key: 'created_at',
          label: 'Created Date',
          sortable: false,
          thClass: ' text-left',
          tdClass: ' text-left',
        },
      ],
      json_fields: {
        'Log Type': {
          field: 'type',
          callback: value => (value ? value.replace(/_/g, ' ') : ''),
        },
        User: {
          callback: value => `"${value.created_by !== null ? `${value.created_by.first_name}` : 'N/A'} ${value.created_by !== null && value.created_by.last_name !== null ? `${value.created_by.last_name}` : ''}"`,
        },
        'Old Data': {
          field: 'old_data',
          callback: value => {
            const logArray = this.getFormattedLogArray(value)
            if (logArray.length === 0) return 'N/A'

            // 1. Join with a semicolon (safer than comma for CSV)
            let combined = logArray.join(' | ')

            // 2. ESCAPE INTERNAL QUOTES: Change " to ""
            // This is the critical part for nested JSON strings
            combined = combined.replace(/"/g, '""')

            // 3. Wrap the whole result in double quotes
            return `"${combined}"`
          },
        },
        'New Data': {
          field: 'new_data',
          callback: value => {
            const logArray = this.getFormattedLogArray(value)
            if (logArray.length === 0) return 'N/A'

            let combined = logArray.join(' | ')

            // Escape internal quotes and wrap
            combined = combined.replace(/"/g, '""')
            return `"${combined}"`
          },
        },
        'Created Date': {
          field: 'created_at',
          // eslint-disable-next-line no-underscore-dangle
          callback: value => this.__dateTimeFormatter(value),
        },
      },
      rows: [],
    }
  },
  computed: {
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true
        return this.dir
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false
      return this.dir
    },
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => ({ text: f.label, value: f.key }))
    },
  },
  watch: {
    page() {
      this.getSortingActivityList()
    },
    perPage() {
      this.getSortingActivityList()
    },
  },
  async mounted() {
    this.totalRows = this.rows.length
  },
  methods: {
    async getSortingActivityList() {
      this.dataLoading = true
      this.rows = []
      const filterQuery = this.buildFilterQuery()
      try {
        const { data } = (await sortingRepository.getSortingActivityList(this.page, this.perPage, filterQuery))
        this.rows = data.data
        this.meta = data.meta
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    async getSortingActivityListNoPagination() {
      try {
        const filterQuery = this.buildFilterQuery()
        const { data } = (await sortingRepository.getSortingActivityListNoPagination(filterQuery))
        return data.data
      } catch (e) {
        this.convertAndNotifyError(e)
        return []
      }
    },
    filterQueryUpdate() {
      this.getSortingActivityList()
    },
    handleChangePage(page) {
      this.page = page
      this.getSortingActivityList()
    },
    changePage(value) {
      this.perPage = value
    },
    sortChanged(e) {
      const key = e.sortBy
      if (key) {
        const active = e.sortDesc ? '-' : ''
        this.sort = active + key
        this.getSortingActivityList()
      }
    },
    onClickRefresh() {
      this.getSortingActivityList()
    },
    getFormattedLogArray(data) {
      if (!data || data === 'null') return []

      let parsed
      try {
        parsed = typeof data === 'string' ? JSON.parse(data) : data
      } catch (e) {
        return [data.toString()]
      }

      if (typeof parsed !== 'object' || parsed === null) return []

      return Object.entries(parsed).map(([key, value]) => {
        // Transform "bucket_node_id" to "Bucket Node Id"
        const formattedKey = key
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')

        // CHECK: If value is an object (and not null), stringify it.
        // Otherwise, handle null/undefined with 'N/A'
        let displayValue
        if (value !== null && typeof value === 'object') {
          displayValue = JSON.stringify(value)
        } else {
          displayValue = value ?? 'N/A'
        }

        return `${formattedKey}: ${displayValue}`
      })
    },
  },
}
</script>

<style lang="scss" scoped>
</style>
